import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import images from "../../images";
import { toast } from "react-toastify";
import {
  usePostFirstUpdateInfoMutation,
  usePostFindNewLocationMutation,
} from "../../services/apiSlice";
import CommonModal from "../../components/common/Modal/ModalCommon";
import Button from "../../components/common/Button/Button";
import LoadingCommon from "../../components/common/LoadingStatus/LoadingCommon";
import {
  GoogleMap,
  Marker,
  useLoadScript,
  Autocomplete,
} from "@react-google-maps/api";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";

// Define libraries as a static constant outside the component
const LIBRARIES = ["places"];

export default function CompanyProfilePage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const companyDataFromProps = location.state?.companyData;
  const [deletedLocationIds, setDeletedLocationIds] = useState([]);

  // Initialize companyData with defaults if not provided
  const [companyData, setCompanyData] = useState(
    companyDataFromProps || {
      company_name: "",
      description: "",
      type_of_food: "",
      website: "",
    }
  );
  const [companyNameError, setCompanyNameError] = useState("");

  const [locations, setLocations] = useState([]);

  const addLocationModalRef = useRef();
  const autocompleteRef = useRef(null); // Ref for Autocomplete

  const [updateCompany, { isLoading: isUpdating }] =
    usePostFirstUpdateInfoMutation();
  const [findNewLocation, { isLoading: isFindingLocation }] =
    usePostFindNewLocationMutation();
  const [newLocation, setNewLocation] = useState({
    name: "",
    country: "",
    city: "",
    district: "",
    address: "",
    latitude: "",
    longitude: "",
  });

  const containerStyle = {
    width: "100%",
    height: "180px",
    borderRadius: "12px",
  };

  useEffect(() => {
    if (companyDataFromProps?.location) {
      const parsedLocation = parseLocationString(companyDataFromProps.location);
      setLocations([{ ...parsedLocation, id: Date.now().toString() }]);
    }
  }, [companyDataFromProps]);

  const parseLocationString = (locationStr) => {
    const parts = locationStr.split(", ");
    return {
      country: parts[parts.length - 1] || "",
      city: parts[parts.length - 2] || "",
      district: parts[parts.length - 3] || "",
      address: parts.slice(0, parts.length - 3).join(", ") || "",
      latitude: null,
      longitude: null,
    };
  };

  const handleInputChange = (e, field) => {
    const value = e.target.value;
    setCompanyData({
      ...companyData,
      [field]: value,
    });
    if (field === "company_name" && value.trim()) {
      setCompanyNameError("");
    }
  };

  const handleNewLocationChange = (e, field) => {
    setNewLocation({
      ...newLocation,
      [field]: e.target.value,
    });
  };

  const handleOpenModal = (locationToEdit = null) => {
    if (locationToEdit) {
      setNewLocation(locationToEdit);
    } else {
      setNewLocation({
        name: "",
        country: "",
        city: "",
        district: "",
        address: "",
        latitude: "",
        longitude: "",
        id: null,
      });
    }
    addLocationModalRef.current.openModal();
  };

  const handleSaveLocation = () => {
    if (!newLocation.address.trim()) {
      toast.error(t("toast.error.pleaseEnterAddress"));
      return;
    }

    const isDuplicate = locations.some(
      (loc) =>
        loc.id !== newLocation.id &&
        (loc.address.toLowerCase().trim() ===
          newLocation.address.toLowerCase().trim() ||
          (loc.name &&
            newLocation.name &&
            loc.name.toLowerCase().trim() ===
              newLocation.name.toLowerCase().trim()))
    );

    if (isDuplicate) {
      toast.error(t("companyProfile.locationAlreadyExists"));
      return;
    }

    const locationData = {
      ...newLocation,
      id: newLocation.id || Date.now().toString(),
    };

    if (newLocation.id) {
      setLocations((prev) =>
        prev.map((loc) => (loc.id === newLocation.id ? locationData : loc))
      );
    } else {
      setLocations((prev) => [...prev, locationData]);
    }

    addLocationModalRef?.current?.closeModal();
  };

  const handlePlaceChanged = () => {
    const place = autocompleteRef.current.getPlace();
    if (place && place.geometry) {
      const addressComponents = place.address_components || [];
      let country = "";
      let city = "";
      let district = "";
      let address = place.formatted_address || "";

      addressComponents.forEach((component) => {
        if (component.types.includes("country")) {
          country = component.long_name;
        }
        if (component.types.includes("locality")) {
          city = component.long_name;
        }
        if (
          component.types.includes("sublocality") ||
          component.types.includes("administrative_area_level_2")
        ) {
          district = component.long_name;
        }
      });

      setNewLocation((prev) => ({
        ...prev,
        address,
        country,
        city,
        district,
        latitude: place.geometry.location.lat().toString(),
        longitude: place.geometry.location.lng().toString(),
      }));
    }
  };

  const handleFindNewLocation = useCallback(async () => {
    try {
      const data = {
        text: newLocation.address,
      };
      const response = await findNewLocation(data).unwrap();

      if (response?.data) {
        setNewLocation((prev) => ({
          ...prev,
          ...response.data,
        }));
      }
    } catch (error) {
      toast.error(t("toast.error.failedToFindLocation"));
    }
  }, [findNewLocation, t, newLocation.address]);

  useEffect(() => {
    if (!newLocation.address) return;

    const debouncedFind = debounce(() => {
      handleFindNewLocation();
    }, 800);

    debouncedFind();

    return () => {
      debouncedFind.cancel();
    };
  }, [newLocation.address, handleFindNewLocation]);

  const handleDeleteLocation = (locationId) => {
    setLocations((prev) => prev.filter((loc) => loc.id !== locationId));
    if (!deletedLocationIds.includes(locationId)) {
      setDeletedLocationIds((prev) => [...prev, locationId]);
    }
  };

  const handleSave = async () => {
    if (!companyData.company_name.trim()) {
      setCompanyNameError(t("companyProfile.pleaseEnterCompanyName"));
      return;
    }
    if (locations.length === 0) {
      toast.error(t("companyProfile.pleaseAddLocation"));
      return;
    }
    const body = {
      company_name: companyData.company_name,
      description: companyData.description,
      type_of_food: companyData.type_of_food,
      website: companyData.website,
      locations: locations,
    };

    try {
      const response = await updateCompany(body).unwrap();
      if (
        response.message === "Operation successful" ||
        response.status === 200
      ) {
        toast.success(t("toast.success.signUpSuccessfully"));
        navigate(`/`);
      }
    } catch (err) {
      toast.error(t("toast.error.failedToUpdateCompanyProfile"));
    }
  };

  const addressMap = {
    lat: Number(newLocation?.latitude) || 10.99835602,
    lng: Number(newLocation?.longitude) || 77.01502627,
  };
  const defaultCenter = {
    lat: 10.99835602,
    lng: 77.01502627,
  };

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
  });

  if (loadError) {
    return <div>{t("companyProfile.errorLoadingMap")}</div>;
  }

  if (!isLoaded) {
    return <LoadingCommon />;
  }

  return (
    <main className="mx-auto w-full bg-yellow-50 max-w-[480px] min-h-screen">
      <Helmet>
        <meta name="theme-color" content="#fefce8" />
      </Helmet>
      <LoadingCommon statusLoading={isUpdating || isFindingLocation} />
      <section className="flex justify-center items-center w-full h-[150px] bg-yellow-50">
        <img src={images.LogoLogin} className="w-[100px]" alt="Logo" />
      </section>
      <section className="flex flex-col justify-between px-6 py-4 w-full bg-white min-h-[calc(100vh-80px)] rounded-t-[30px] shadow-[0px_0px_4px_rgba(0,0,0,0.08)]">
        <div className="flex flex-col w-full mx-auto">
          <section className="w-full text-left mb-4">
            <div className="flex items-center gap-4">
              <img
                src={images.BackIconGray}
                className="w-5 h-4 cursor-pointer"
                alt="backIcon"
                onClick={() => navigate(-1)}
              />
              <span className="text-lg font-medium text-gray-800">
                {companyData.company_name || t("companyProfile.companyName")}
              </span>
            </div>
          </section>

          <div className="flex flex-col w-full text-sm text-neutral-500">
            <div className="mb-4">
              <span className="flex items-center text-sm text-gray-400 mb-1">
                <img
                  src={images.BuildingIcon}
                  className="w-[22px] mr-2"
                  alt=""
                />
                {t("companyProfile.companyName")}
              </span>
              <input
                type="text"
                className={`w-full p-2 border rounded-md ${
                  companyNameError ? "border-red-500" : "border-gray-200"
                }`}
                value={companyData.company_name || ""}
                onChange={(e) => handleInputChange(e, "company_name")}
                placeholder={t("companyProfile.enterCompanyName")}
              />
              {companyNameError && (
                <p className="text-red-500 text-xs mt-1">{companyNameError}</p>
              )}
            </div>

            <div className="mb-4">
              <span className="flex items-center text-sm text-gray-400 mb-1">
                <img
                  src={images.BookmarkIcon}
                  className="w-[22px] mr-2"
                  alt=""
                />
                {t("companyProfile.description")}
              </span>
              <textarea
                className="w-full p-2 border border-gray-200 rounded-md min-h-[100px]"
                value={companyData.description || ""}
                onChange={(e) => handleInputChange(e, "description")}
                placeholder={t("companyProfile.enterDescription")}
              />
            </div>

            <div className="mb-4">
              <span className="flex items-center text-sm text-gray-400 mb-1">
                <img src={images.TagIcon} className="w-[22px] mr-2" alt="" />
                {t("companyProfile.typeOfFood")}
              </span>
              <textarea
                className="w-full p-2 border border-gray-200 rounded-md min-h-[100px]"
                value={companyData.type_of_food || ""}
                onChange={(e) => handleInputChange(e, "type_of_food")}
                placeholder={t("companyProfile.enterTypeOfFood")}
              />
            </div>

            <div className="mb-4">
              <span className="flex items-center text-sm text-gray-400 mb-1">
                <img src={images.GlobalIcon} className="w-[22px] mr-2" alt="" />
                {t("companyProfile.website")}
              </span>
              <input
                type="text"
                className="w-full p-2 border border-gray-200 rounded-md"
                value={companyData.website || ""}
                onChange={(e) => handleInputChange(e, "website")}
                placeholder={t("companyProfile.enterWebsite")}
              />
            </div>

            <div className="mb-4">
              <span className="flex items-center justify-between text-sm text-gray-400 mb-1">
                <div className="flex items-center">
                  <img
                    src={images.MapPointIcon}
                    className="w-[22px] mr-2"
                    alt=""
                  />
                  {t("companyProfile.location")}
                </div>
                <span
                  className="p-1 border rounded-md flex items-center justify-between text-sm text-gray-400 cursor-pointer"
                  onClick={() => handleOpenModal()}
                >
                  <img
                    src={images.AddCircleIcon}
                    className="w-[22px] mr-1"
                    alt=""
                  />
                  {t("base.add")}
                </span>
              </span>
              <ul className="mt-1.5 space-y-1.5">
                {locations
                  .slice()
                  .sort((a, b) => {
                    const nameA = a.name
                      ? a.name.toLowerCase()
                      : a.address.toLowerCase();
                    const nameB = b.name
                      ? b.name.toLowerCase()
                      : b.address.toLowerCase();
                    return nameA.localeCompare(nameB);
                  })
                  .map((location) => (
                    <li
                      key={location.id}
                      className="flex justify-between items-center px-2.5 py-1.5 w-full text-sm rounded-xl bg-gray-100"
                    >
                      <span
                        className="cursor-pointer hover:text-indigo-600"
                        onClick={() => handleOpenModal(location)}
                      >
                        {location.name ? location.name : location.address}
                      </span>
                      <img
                        src={images.TrashLocationIcon}
                        alt="Delete"
                        className="w-[24px] h-[24px] cursor-pointer hover:opacity-75"
                        onClick={() => handleDeleteLocation(location.id)}
                      />
                    </li>
                  ))}
              </ul>
            </div>

            <button
              onClick={handleSave}
              disabled={isUpdating}
              className={`w-full px-4 py-3 mt-5 text-lg font-semibold text-white rounded-xl ${
                isUpdating ? "bg-gray-400" : "bg-indigo-600"
              }`}
            >
              {isUpdating ? t("base.saving") : t("base.save")}
            </button>
          </div>
        </div>
      </section>

      {/* Modal để thêm địa chỉ mới */}
      <CommonModal
        ref={addLocationModalRef}
        title={
          newLocation.id
            ? t("companyProfile.editLocation")
            : t("companyProfile.addNewLocation")
        }
        actions={(closeModal) => (
          <>
            <Button
              text={t("base.cancel")}
              className="flex-1 px-5 py-3 rounded-xl bg-white border border-stone-200 text-neutral-500"
              onClick={() => closeModal(false)}
            />
            <Button
              text={t("base.save")}
              className="flex-1 px-5 py-3 rounded-xl text-white bg-indigo-600"
              variant="primary"
              onClick={handleSaveLocation}
            />
          </>
        )}
      >
        <div className="flex flex-col gap-3">
          <input
            type="text"
            className="w-full p-2 border border-gray-200 rounded-md"
            placeholder={t("profile.locationName")}
            value={newLocation.name}
            onChange={(e) => handleNewLocationChange(e, "name")}
          />

          <Autocomplete
            onLoad={(autocomplete) => {
              autocompleteRef.current = autocomplete;
            }}
            onPlaceChanged={handlePlaceChanged}
          >
            <div className="relative w-full">
              <input
                type="text"
                className="w-full p-2 border border-gray-200 rounded-md"
                placeholder={t("profile.searchAddress")}
                value={newLocation.address}
                onChange={(e) => handleNewLocationChange(e, "address")}
              />
              <img
                className="absolute w-[14px] h-[14px] top-1/2 right-2 transform -translate-y-1/2 cursor-pointer"
                src={images.SearchIcon}
                alt=""
                onClick={handleFindNewLocation}
              />
            </div>
          </Autocomplete>
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={
              newLocation?.latitude && newLocation?.longitude
                ? addressMap
                : defaultCenter
            }
            zoom={15}
            onLoad={(map) => {}}
          >
            {newLocation?.latitude && newLocation?.longitude && (
              <Marker
                position={addressMap}
                icon={{
                  url: images.DotMapIconBlue,
                  scaledSize: new window.google.maps.Size(40, 40),
                }}
              />
            )}
          </GoogleMap>
        </div>
      </CommonModal>
    </main>
  );
}
